import * as THREE from "three";
import { EffectComposer } from "three/addons/postprocessing/EffectComposer.js";
import { OutputPass } from "three/addons/postprocessing/OutputPass.js";
import { RenderPass } from "three/addons/postprocessing/RenderPass.js";
import { UnrealBloomPass } from "three/addons/postprocessing/UnrealBloomPass.js";
import { buildEnvironment, updateFlames, type Flame } from "./environment";
import { buildTable, TABLE_CENTER_Z } from "./table";
import { Tweens } from "./tween";

/** Champ de vision vertical de base (format large) et seuil d'aspect en dessous duquel on l'elargit. */
const BASE_FOV = 46;
const BASE_ASPECT = 1.35;

/**
 * Trois niveaux de rendu, du plus riche au plus econome — meme ambiance (bois,
 * bougies, cheminee), juste moins de pixels/ombres/lumiere/post-traitement a
 * calculer en continu. Reglable a tout moment (menu ou en cours de partie, cf.
 * `setQuality`) : l'antialiasing reste donc fixe (il ne peut changer qu'a la
 * creation du contexte WebGL), seuls la resolution, les ombres, le bloom, le
 * vacillement des flammes et le plafond de cadence varient d'un niveau a l'autre.
 */
export type GraphicsQuality = "high" | "standard" | "battery";

interface QualityConfig {
  readonly pixelRatioCap: number;
  readonly shadowsEnabled: boolean;
  readonly shadowMapSize: number;
  /** Halo bloom (UnrealBloomPass) : une passe plein ecran de plus, en sus du rendu de base. */
  readonly bloomEnabled: boolean;
  /** Cadence de rendu plafonnee (images/s) ; `null` = pas de plafond (rendu a chaque frame). */
  readonly targetFps: number | null;
  /** Flammes qui vacillent (mise a jour lumiere/sprite chaque frame) ou flamme fixe (intensite/echelle de creation). */
  readonly animateFlames: boolean;
}

// "standard" est desormais nettement plus econome que "high" (bloom coupe,
// resolution et cadence plafonnees) : un solitaire n'a besoin ni de 120 im/s
// ni d'un plein pixel-ratio retina, et faire tourner un GPU mobile a fond en
// continu chauffe le telephone pour un gain visuel quasi nul sur ce jeu.
// "high" reste le niveau "tout active" pour qui le demande explicitement.
const QUALITY_CONFIGS: Record<GraphicsQuality, QualityConfig> = {
  high: { pixelRatioCap: 2, shadowsEnabled: true, shadowMapSize: 1024, bloomEnabled: true, targetFps: null, animateFlames: true },
  standard: { pixelRatioCap: 1.5, shadowsEnabled: true, shadowMapSize: 768, bloomEnabled: false, targetFps: 60, animateFlames: true },
  battery: { pixelRatioCap: 1, shadowsEnabled: false, shadowMapSize: 512, bloomEnabled: false, targetFps: 30, animateFlames: false },
};

/**
 * Position de repos de la camera : vue quasiment verticale et rapprochee,
 * pour que le tapis (cf. render/table.ts) remplisse tout le cadre et que
 * les cartes se lisent le plus grand possible — pas d'adversaire en face a
 * montrer, cahier des charges §3. L'angle, presque a la perpendiculaire,
 * minimise aussi la deformation en trapeze du tapis rectangulaire
 * (presentation plus simple et plus lisible). La cible est decalee au-dela
 * de TABLE_CENTER_Z (et non pile dessus) : la camera penche un peu plus
 * vers l'horizon, ce qui laisse entrevoir le decor de taverne en haut du
 * cadre et pousse le tapis vers le bas de l'ecran, sans changer le zoom
 * (meme position de camera, seule l'orientation change).
 */
const CAMERA_HOME = new THREE.Vector3(0, 6.2, 0.2);
const CAMERA_TARGET = new THREE.Vector3(0, 0, TABLE_CENTER_Z - 0.7);

/**
 * Scene 3D complete : rendu, camera fixe en plongee prononcee (vue solitaire,
 * presque subjective), eclairage bougies + cheminee (flammes vacillantes),
 * post-traitement bloom chaleureux, table et decor de taverne. Meme
 * architecture que la TavernScene de dkg-battle : la boucle de rendu est
 * geree en interne, le controleur s'abonne via `onFrame` et sequence ses
 * animations avec `tweens`.
 */
export class TavernScene {
  readonly scene = new THREE.Scene();
  readonly camera: THREE.PerspectiveCamera;
  readonly renderer: THREE.WebGLRenderer;
  readonly tweens = new Tweens();

  private composer: EffectComposer | null;
  private bloomPass: UnrealBloomPass | null = null;
  private flames: Flame[];
  private clock = new THREE.Clock();
  private frameCallbacks: ((dt: number, time: number) => void)[] = [];
  private tableLight: THREE.PointLight;
  private quality: QualityConfig;
  private frameAccumulator = 0;

  constructor(private container: HTMLElement, quality: GraphicsQuality = "standard") {
    this.quality = QUALITY_CONFIGS[quality];

    this.renderer = new THREE.WebGLRenderer({ antialias: true });
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, this.quality.pixelRatioCap));
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
    this.renderer.toneMappingExposure = 1.1;
    // Ombres douces : les cartes qui se chevauchent (main, piles) doivent se
    // distinguer par une ombre de contact, pas seulement par la hauteur.
    this.renderer.shadowMap.enabled = this.quality.shadowsEnabled;
    this.renderer.shadowMap.type = THREE.PCFShadowMap;
    // Ombres recalculees seulement quand quelque chose bouge (cf. `tick`), pas a
    // chaque frame indefiniment : la table est statique la plupart du temps (le
    // joueur/l'IA reflechit), inutile de refaire la passe d'ombres en boucle.
    this.renderer.shadowMap.autoUpdate = false;
    this.renderer.shadowMap.needsUpdate = true;
    container.appendChild(this.renderer.domElement);

    this.camera = new THREE.PerspectiveCamera(BASE_FOV, 1, 0.1, 60);
    this.camera.position.copy(CAMERA_HOME);
    this.camera.lookAt(CAMERA_TARGET);

    // Brouillard sombre : fond peu contraste, l'oeil reste sur la table (CDC §3).
    // Le bord le plus lointain de la table est a ~12 du point de vue : le
    // brouillard ne demarre qu'au-dela, il n'assombrit que le decor.
    this.scene.fog = new THREE.Fog(new THREE.Color("#0d0805"), 13, 26);
    this.scene.background = new THREE.Color("#0d0805");

    // Base d'eclairage un peu plus presente : avec un point lumineux de table
    // beaucoup moins intense (cf. plus bas), l'ambiant porte une plus grande
    // part de la lisibilite generale sans pour autant aplatir la scene.
    this.scene.add(new THREE.AmbientLight("#3a2a1a", 1.5));

    // "Chandelier" invisible au-dessus de la table : la table doit etre
    // nettement plus lumineuse que le reste de la salle (CDC §23). Un point
    // lumineux proche d'un grand plan horizontal, vu par une camera oblique,
    // projette un halo de perspective ETIRE A L'ECRAN (fondamentalement
    // correct optiquement) — cadre par les joints de planches, un point trop
    // intense se lit a tort comme une bande lumineuse parasite. On reste
    // donc volontairement discret sur l'intensite et le decay, et on
    // remonte l'ambiant pour garder une table lisible malgre tout.
    // Centre sur TABLE_CENTER_Z (et non l'origine du monde) : la "lumiere
    // centrale" doit tomber au milieu du tapis de jeu, pas d'un point
    // arbitraire du feutre (desormais demesure, cf. render/table.ts).
    this.tableLight = new THREE.PointLight("#ffab55", 13, 0, 1);
    this.tableLight.position.set(0, 6, TABLE_CENTER_Z);
    this.tableLight.castShadow = this.quality.shadowsEnabled;
    this.tableLight.shadow.mapSize.set(this.quality.shadowMapSize, this.quality.shadowMapSize);
    this.tableLight.shadow.bias = -0.0015;
    this.tableLight.shadow.camera.near = 1;
    this.tableLight.shadow.camera.far = 9;
    // Ombres adoucies : avec une seule lumiere forte et un ambiant faible, une
    // ombre a pleine intensite tombe a un noir dur — on en laisse passer un peu.
    // Relevee par rapport a la premiere version (0.55) : trop discrete pour
    // se voir nettement sous une carte soulevee (glisser, resolution auto).
    this.tableLight.shadow.intensity = 0.78;
    this.scene.add(this.tableLight);

    this.scene.add(buildTable());
    const env = buildEnvironment();
    this.flames = env.flames;
    this.scene.add(env.group);

    // Post-traitement (bloom) : une passe plein ecran de plus par-dessus le rendu de
    // base, sautee en mode "battery" (cf. QUALITY_CONFIGS) — `tick()` retombe alors
    // sur `renderer.render(...)` directement, sans composer du tout.
    this.composer = this.buildComposer();

    this.resize();
    window.addEventListener("resize", () => this.resize());
    this.renderer.setAnimationLoop(() => this.tick());
  }

  /** Abonne un callback appele a chaque frame (dt et temps ecoule, en secondes). */
  onFrame(callback: (dt: number, time: number) => void): void {
    this.frameCallbacks.push(callback);
  }

  /** Construit la chaine de post-traitement (bloom), ou `null` si le niveau de
   * rendu courant ne la demande pas (cf. QUALITY_CONFIGS). Appelee a la creation
   * et a chaque changement de niveau (cf. `setQuality`). */
  private buildComposer(): EffectComposer | null {
    if (!this.quality.bloomEnabled) return null;
    const renderPass = new RenderPass(this.scene, this.camera);
    // Bloom discret : halo chaud autour des flammes et des cartes eclairees,
    // sans transformer la taverne en boite de nuit (CDC §22 : sobriete).
    const bloom = new UnrealBloomPass(new THREE.Vector2(1, 1), 0.35, 0.6, 0.82);
    this.bloomPass = bloom;
    // Cible de rendu MULTI-ECHANTILLONNEE explicite : sans elle, l'EffectComposer
    // cree sa propre render target par defaut SANS multisampling, ce qui annule
    // le lissage `antialias: true` du renderer des qu'une passe de post-traitement
    // est active — les bords des cartes (coins arrondis, texte grave) redeviennent
    // visiblement crenelés. 2 echantillons (pas 4) : ce niveau ("high" uniquement,
    // cf. QUALITY_CONFIGS) reste deja le plus coûteux du jeu, inutile d'ajouter
    // une charge GPU de MSAA excessive sur un appareil mobile.
    const size = this.renderer.getDrawingBufferSize(new THREE.Vector2());
    const renderTarget = new THREE.WebGLRenderTarget(size.width, size.height, { samples: 2 });
    const composer = new EffectComposer(this.renderer, renderTarget);
    composer.addPass(renderPass);
    composer.addPass(bloom);
    composer.addPass(new OutputPass());
    return composer;
  }

  /**
   * Change de niveau de rendu a chaud, y compris en cours de partie (pas seulement
   * au demarrage) : resolution, ombres (activation + resolution), bloom, vacillement
   * des flammes et plafond de cadence sont tous modifiables sans reconstruire le
   * renderer ni le canvas. Seul l'antialiasing reste fixe (fige a la creation du
   * contexte WebGL par three.js), d'ou son absence de `QualityConfig`.
   */
  setQuality(quality: GraphicsQuality): void {
    this.quality = QUALITY_CONFIGS[quality];

    // Resolution : `setPixelRatio` seul ne redimensionne pas le tampon de dessin
    // existant, il faut redeclencher un `resize()` (fait a la fin, une fois tous
    // les autres reglages a jour).
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, this.quality.pixelRatioCap));

    // Ombres : la render target existante reste a l'ancienne resolution tant qu'on
    // ne la jette pas explicitement — three.js en recree une (a la bonne taille)
    // au prochain rendu si des ombres sont a nouveau demandees.
    this.tableLight.shadow.map?.dispose();
    this.tableLight.shadow.map = null;
    this.tableLight.castShadow = this.quality.shadowsEnabled;
    this.renderer.shadowMap.enabled = this.quality.shadowsEnabled;
    if (this.quality.shadowsEnabled) {
      this.tableLight.shadow.mapSize.set(this.quality.shadowMapSize, this.quality.shadowMapSize);
      this.renderer.shadowMap.needsUpdate = true;
    }

    this.frameAccumulator = 0;

    if (!this.quality.animateFlames) {
      // Fige les flammes a leur intensite/echelle de repos (valeurs de creation),
      // plutot que de les laisser au milieu d'un vacillement (etat fige arbitraire sinon).
      for (const flame of this.flames) {
        flame.light.intensity = flame.baseIntensity;
        flame.sprite.scale.setScalar(flame.sprite.userData.baseScale as number);
      }
    }

    // Bloom : la chaine de post-traitement se reconstruit (ou se demonte) entierement
    // — pas de simple bascule d'activation, la passe resterait allouee et couterait
    // son plein tarif tant qu'elle existe dans le composer.
    this.bloomPass?.dispose();
    this.bloomPass = null;
    this.composer?.dispose();
    this.composer = this.buildComposer();

    this.resize();
  }

  private resize(): void {
    const width = this.container.clientWidth || window.innerWidth;
    const height = this.container.clientHeight || window.innerHeight;
    const aspect = Math.max(width / Math.max(height, 1), 0.01);
    this.camera.aspect = aspect;

    // Sur ecran etroit (mobile portrait), le champ vertical de base (calibre
    // pour le format large) ferait sortir les bords du plateau du cadre : on
    // garde constant le champ *horizontal* qu'on a en format large, ce qui
    // elargit le champ vertical juste ce qu'il faut pour que le plateau
    // entier — les 7 colonnes, jusqu'au bord — reste visible (meme technique
    // que dkg-farkle). Le plafond est volontairement tres haut : avec une
    // camera quasi verticale au-dessus d'un plateau plat (pas de profondeur
    // 3D marquee dans le cadre), un grand champ de vision ne produit pas le
    // fisheye qu'on aurait sur une scene en relief — mieux vaut un champ
    // large que des colonnes coupees sur les telephones les plus etroits.
    if (aspect < BASE_ASPECT) {
      const halfHorizontal = Math.tan(THREE.MathUtils.degToRad(BASE_FOV / 2)) * BASE_ASPECT;
      this.camera.fov = Math.min(2 * THREE.MathUtils.radToDeg(Math.atan(halfHorizontal / aspect)), 120);
    } else {
      this.camera.fov = BASE_FOV;
    }

    this.camera.updateProjectionMatrix();
    this.renderer.setSize(width, height);
    this.composer?.setSize(width, height);
  }

  private tick(): void {
    const dt = Math.min(this.clock.getDelta(), 0.1);

    // Mode "battery" : cadence plafonnee (30 im/s) plutot que la cadence native de
    // l'ecran (souvent 60-120 im/s sur mobile) — la moitie (voire le quart) moins
    // d'images a calculer, pour un jeu de cartes ou la fluidite maximale n'apporte
    // rien. Le temps ecoule s'accumule meme sur les frames sautees, pour que les
    // tweens/physiques restent a la bonne vitesse (pas de ralenti) une fois la
    // frame rendue.
    let elapsed = dt;
    if (this.quality.targetFps !== null) {
      this.frameAccumulator += dt;
      const targetInterval = 1 / this.quality.targetFps;
      if (this.frameAccumulator < targetInterval) return;
      elapsed = this.frameAccumulator;
      this.frameAccumulator = 0;
    }

    const time = this.clock.elapsedTime;
    const tweenActive = this.tweens.update(elapsed);

    if (this.quality.animateFlames) {
      updateFlames(this.flames, time);
    }

    for (const cb of this.frameCallbacks) cb(elapsed, time);

    if (this.quality.shadowsEnabled && tweenActive) {
      this.renderer.shadowMap.needsUpdate = true;
    }

    if (this.composer) {
      this.composer.render();
    } else {
      this.renderer.render(this.scene, this.camera);
    }
  }
}
